<!--
  SPDX-License-Identifier: MIT
  Copyright (c) 2026 Solvetia. Original work by Solvetia — part of the Solvetia
  white-label additions to Chatwoot Community Edition (MIT). Not derived from
  the commercial code under enterprise/.
-->
<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { AVAILABLE_CUSTOM_ROLE_PERMISSIONS } from 'dashboard/constants/permissions';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { buildAccountRolePayload } from './helpers';

const blankFlags = () =>
  AVAILABLE_CUSTOM_ROLE_PERMISSIONS.reduce((acc, key) => {
    acc[key] = false;
    return acc;
  }, {});

export default {
  components: {
    NextButton,
  },
  data() {
    return {
      availablePermissions: AVAILABLE_CUSTOM_ROLE_PERMISSIONS,
      editingId: null,
      name: '',
      description: '',
      permissionFlags: blankFlags(),
    };
  },
  computed: {
    ...mapGetters({
      roles: 'accountRoles/getAccountRoles',
      uiFlags: 'accountRoles/getUIFlags',
    }),
  },
  mounted() {
    this.$store.dispatch('accountRoles/get');
  },
  methods: {
    resetForm() {
      this.editingId = null;
      this.name = '';
      this.description = '';
      this.permissionFlags = blankFlags();
    },
    startEdit(role) {
      this.editingId = role.id;
      this.name = role.name;
      this.description = role.description || '';
      this.permissionFlags = blankFlags();
      (role.permissions || []).forEach(key => {
        if (key in this.permissionFlags) this.permissionFlags[key] = true;
      });
    },
    async save() {
      const payload = buildAccountRolePayload(this.$data);
      try {
        if (this.editingId) {
          await this.$store.dispatch('accountRoles/update', {
            id: this.editingId,
            ...payload,
          });
        } else {
          await this.$store.dispatch('accountRoles/create', payload);
        }
        useAlert(this.$t('ACCOUNT_ROLES.SAVED'));
        this.resetForm();
      } catch (e) {
        useAlert(this.$t('ACCOUNT_ROLES.ERROR'));
      }
    },
    async remove(role) {
      try {
        await this.$store.dispatch('accountRoles/delete', role.id);
        useAlert(this.$t('ACCOUNT_ROLES.DELETED'));
        if (this.editingId === role.id) this.resetForm();
      } catch (e) {
        useAlert(this.$t('ACCOUNT_ROLES.ERROR'));
      }
    },
  },
};
</script>

<template>
  <div class="flex flex-col gap-6 p-6 max-w-2xl">
    <h2 class="text-xl font-medium text-n-slate-12">
      {{ $t('ACCOUNT_ROLES.TITLE') }}
    </h2>
    <p class="text-sm text-n-slate-11">{{ $t('ACCOUNT_ROLES.DESCRIPTION') }}</p>

    <!-- Existing roles -->
    <ul v-if="roles.length" class="flex flex-col gap-2">
      <li
        v-for="role in roles"
        :key="role.id"
        class="flex items-center justify-between gap-3 p-3 rounded-lg bg-n-alpha-2"
      >
        <span class="flex flex-col">
          <span class="text-sm font-medium text-n-slate-12">{{
            role.name
          }}</span>
          <span class="text-xs text-n-slate-10">{{
            (role.permissions || []).join(', ')
          }}</span>
        </span>
        <span class="flex items-center gap-2">
          <NextButton
            slate
            ghost
            xs
            :label="$t('ACCOUNT_ROLES.EDIT')"
            @click="startEdit(role)"
          />
          <NextButton
            ruby
            ghost
            xs
            :label="$t('ACCOUNT_ROLES.DELETE')"
            @click="remove(role)"
          />
        </span>
      </li>
    </ul>

    <!-- Create / edit form -->
    <div class="flex flex-col gap-4 pt-4 border-t border-n-weak">
      <h3 class="text-base font-medium text-n-slate-12">
        {{
          editingId
            ? $t('ACCOUNT_ROLES.EDIT_TITLE')
            : $t('ACCOUNT_ROLES.NEW_TITLE')
        }}
      </h3>

      <label class="flex flex-col gap-1">
        <span class="text-sm text-n-slate-11">{{
          $t('ACCOUNT_ROLES.NAME')
        }}</span>
        <input
          v-model="name"
          type="text"
          :placeholder="$t('ACCOUNT_ROLES.NAME_PLACEHOLDER')"
        />
      </label>

      <label class="flex flex-col gap-1">
        <span class="text-sm text-n-slate-11">{{
          $t('ACCOUNT_ROLES.DESCRIPTION_LABEL')
        }}</span>
        <input
          v-model="description"
          type="text"
          :placeholder="$t('ACCOUNT_ROLES.DESCRIPTION_PLACEHOLDER')"
        />
      </label>

      <span class="flex flex-col gap-2">
        <span class="text-sm text-n-slate-11">{{
          $t('ACCOUNT_ROLES.PERMISSIONS')
        }}</span>
        <label
          v-for="permission in availablePermissions"
          :key="permission"
          class="flex items-center gap-2"
        >
          <input v-model="permissionFlags[permission]" type="checkbox" />
          <span class="text-sm text-n-slate-12">{{
            $t(`ACCOUNT_ROLES.PERMISSION_LABELS.${permission}`)
          }}</span>
        </label>
      </span>

      <span class="flex items-center gap-2">
        <NextButton
          blue
          :is-loading="uiFlags.isCreating || uiFlags.isUpdating"
          :label="$t('ACCOUNT_ROLES.SUBMIT')"
          @click="save"
        />
        <NextButton
          v-if="editingId"
          slate
          ghost
          :label="$t('ACCOUNT_ROLES.CANCEL')"
          @click="resetForm"
        />
      </span>
    </div>
  </div>
</template>
